import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Box, Typography, LinearProgress, Alert } from '@mui/material';
import { CloudUpload, CheckCircle, Error } from '@mui/icons-material';
import * as Actions from 'actions';

const ContactUploader = (props) => {
  const { onUploadSuccess, isLoading, uploadResult, serviceError, reqStatus, uploadContacts, sellerContactsReset } = props;
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const validateAndSetFile = (file) => {
    const validTypes = [
      'text/csv',
      'text/vcard',
      'text/x-vcard',
      'text/x-vcalendar',
      'application/vnd.ms-excel',
    ];
    const validExtensions = ['.csv', '.vcf', '.vcard'];
    
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (validTypes.includes(file.type) || hasValidExtension) {
      setFile(file);
    } else {
      alert('Please upload a CSV or vCard/VCF file');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleUpload = () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    
    uploadContacts(formData);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  useEffect(() => {
    // If upload was successful, clear the file and refresh contacts
    if (reqStatus === 'SELLER_UPLOAD_CONTACTS_SUCCESS') {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refresh contacts list
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    }
  }, [reqStatus, onUploadSuccess]);

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload Contacts
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Upload a CSV or vCard/VCF file containing your contacts
      </Typography>

      {/* Drag and Drop Area */}
      <Box
        sx={{
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: dragging ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          Drag and drop your contact file here
        </Typography>
        <Typography variant="body2" color="textSecondary">
          or click to browse
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 2 }}>
          Supported formats: CSV, VCF (vCard)
        </Typography>
      </Box>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.vcf,.vcard,text/csv,text/vcard,text/x-vcard"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Selected File */}
      {file && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2">
            Selected file: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
          </Typography>
        </Box>
      )}

      {/* Upload Button */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={!file || isLoading}
          onClick={handleUpload}
          startIcon={<CloudUpload />}
        >
          {isLoading ? 'Uploading...' : 'Upload Contacts'}
        </Button>
      </Box>

      {/* Loading Progress */}
      {isLoading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Success Message */}
      {uploadResult && !isLoading && (
        <Alert
          severity="success"
          icon={<CheckCircle />}
          sx={{ mt: 2 }}
          onClose={() => sellerContactsReset()}
        >
          <Typography variant="body2" gutterBottom>
            <strong>Upload Successful!</strong>
          </Typography>
          <Typography variant="caption" display="block">
            Total contacts: {uploadResult.total_contacts}
          </Typography>
          <Typography variant="caption" display="block">
            Created: {uploadResult.created} | Updated: {uploadResult.updated}
          </Typography>
          <Typography variant="caption" display="block">
            New users: {uploadResult.users_created} | Subscriptions: {uploadResult.subscriptions_created}
          </Typography>
          {(uploadResult.failed > 0 || uploadResult.skipped > 0) && (
            <Box sx={{ mt: 1 }}>
              {uploadResult.skipped > 0 && (
                <Typography variant="caption" display="block" color="warning.main">
                  Skipped (invalid mobile): {uploadResult.skipped}
                </Typography>
              )}
              {uploadResult.failed > 0 && (
                <Typography variant="caption" display="block" color="error">
                  Failed: {uploadResult.failed}
                </Typography>
              )}
            </Box>
          )}
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                Error Details:
              </Typography>
              {uploadResult.errors.slice(0, 10).map((err, idx) => (
                <Typography key={idx} variant="caption" display="block" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                  • {err.contact} ({err.mobile || 'N/A'}): {err.error}
                </Typography>
              ))}
              {uploadResult.errors.length > 10 && (
                <Typography variant="caption" display="block" fontStyle="italic">
                  ... and {uploadResult.errors.length - 10} more
                </Typography>
              )}
            </Box>
          )}
        </Alert>
      )}

      {/* Error Message */}
      {serviceError && !isLoading && (
        <Alert
          severity="error"
          icon={<Error />}
          sx={{ mt: 2 }}
          onClose={() => sellerContactsReset()}
        >
          {serviceError}
        </Alert>
      )}

      {/* Instructions */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          CSV Format Example:
        </Typography>
        <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
          {`name,mobile,email,address
John Doe,9876543210,john@example.com,123 Main St
Jane Smith,8765432109,jane@example.com,456 Oak Ave`}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          <strong>Important:</strong> Each contact must have a valid mobile number. Entries with invalid or missing mobile numbers will be skipped.
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          For vCard/VCF files, you can export directly from your phone's contacts app.
        </Typography>
      </Box>
    </Box>
  );
};

const _ContactUploader = connect(
  ({ sellerContacts }) => ({
    isLoading: sellerContacts.isLoading,
    uploadResult: sellerContacts.uploadResult,
    serviceError: sellerContacts.serviceError,
    reqStatus: sellerContacts.reqStatus,
  }),
  (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(ContactUploader);

export { _ContactUploader as default };
